import React, { memo } from 'react';
import { FadeIn } from './ui/FadeIn';
import { Check, ArrowRight } from 'lucide-react';
export function SalesLetter() {
  return <article className="max-w-2xl mx-auto px-6 py-16 space-y-16 md:space-y-24" data-id="element-129">
      {/* Hook Section */}
      <section className="space-y-4" data-id="element-130">
        <FadeIn data-id="element-131">
          <p className="first-letter:text-5xl first-letter:font-bold first-letter:text-[#7F1D1D] first-letter:mr-2 first-letter:float-left mb-4" data-id="element-132">
            Hay una forma de leer las noticias que te deja agotado.
          </p>
          <p className="mb-4" data-id="element-133">
            Lees un titular. Luego otro. Luego un hilo. Luego un "análisis" de
            cinco minutos.
          </p>
          <p className="mb-4" data-id="element-134">
            Y aun así, al final del día, sientes lo mismo: que algo grande está
            pasando, pero nadie te lo está explicando completo.
          </p>
          <p className="mb-4" data-id="element-135">
            Te dan fragmentos. Te dan etiquetas. Te dan bandos.
          </p>
          <p className="mb-4" data-id="element-136">
            Te dicen: "dictadura", "sanciones", "corrupción". Te lo repiten
            tanto que suena a explicación.
          </p>
          <p className="font-bold text-[#7F1D1D] mb-4" data-id="element-137">Pero no lo es.</p>
        </FadeIn>

        <FadeIn data-id="element-138">
          <p className="mb-4" data-id="element-139">
            Porque cuando miras el mundo con honestidad intelectual, hay una
            pregunta que no se va:
          </p>
          <blockquote className="pl-6 border-l-4 border-[#7F1D1D] italic text-xl my-8 text-[#1C1917]" data-id="element-140">
            ¿Por qué Venezuela sigue siendo central, incluso cuando parece
            aislada?
          </blockquote>
          <p className="mb-4" data-id="element-141">
            Y si esa pregunta te persigue, no es por curiosidad morbosa. Es por
            instinto.
          </p>
          <p className="mb-4" data-id="element-142">
            Instinto de que el siglo XXI no se entiende solo con discursos,
            elecciones o escándalos.
          </p>
          <p className="mb-4" data-id="element-143">Se entiende con algo más frío.</p>
          <p className="mb-4" data-id="element-144">Más estructural.</p>
          <p className="mb-4" data-id="element-145">Más incómodo.</p>
          <p className="mb-4" data-id="element-146">
            Se entiende mirando lo que casi nunca ponen en la pantalla:
          </p>
          <p className="font-bold text-lg tracking-wide uppercase text-[#1C1917] mt-4" data-id="element-147">
            energía, minerales, rutas, contratos, sanciones, dependencia.
          </p>
        </FadeIn>
      </section>

      {/* The Map */}
      <section className="space-y-4" data-id="element-148">
        <FadeIn data-id="element-149">
          <h3 className="text-2xl font-sans font-bold text-[#1C1917] mb-6" data-id="element-150">
            En otras palabras: el tablero.
          </h3>
          <p className="mb-4" data-id="element-151">Lo que te falta no es información. Es un mapa.</p>
          <p className="mb-4" data-id="element-152">Aquí es donde la mayoría se queda atrapada.</p>
          <p className="mb-4" data-id="element-153">
            No por falta de inteligencia. No por falta de interés. Por falta de
            estructura.
          </p>
          <p className="mb-4" data-id="element-154">
            Consumes información, pero no entiendes. Opinas, pero dudas. Sientes
            ruido, no criterio.
          </p>
          <p className="mb-4" data-id="element-155">Y lo peor es que el ruido desgasta.</p>
          <p className="mb-4" data-id="element-156">Te vuelve cínico. O te vuelve reactivo.</p>
          <p className="mb-4" data-id="element-157">
            Un día te descubres repitiendo frases que no elegiste. Defendiendo
            posiciones que no terminaste de pensar. O callándote porque sientes
            que no tienes "todo" para hablar.
          </p>
          <p className="mb-4" data-id="element-158">
            Si te suena familiar, no es un defecto personal.
          </p>
          <p className="mb-4" data-id="element-159">
            Es el resultado de cómo nos "sirven" el mundo hoy: por pedazos.
          </p>
          <p className="mb-4" data-id="element-160">
            Y los pedazos nunca te muestran lo que manda de verdad.
          </p>
        </FadeIn>
      </section>

      {/* Energy Lens */}
      <section className="space-y-4" data-id="element-161">
        <FadeIn data-id="element-162">
          <h2 className="text-3xl font-sans font-bold text-[#7F1D1D] mb-8 border-b border-[#1C1917]/10 pb-4" data-id="element-163">
            El ángulo que casi nadie te da: el lente energético
          </h2>
          <p className="mb-4" data-id="element-164">
            Hay países que se gobiernan desde palacios. Otros, desde embajadas.
          </p>
          <p className="mb-4" data-id="element-165">
            Y hay países que se explican mejor desde el subsuelo.
          </p>
          <p className="font-bold mb-4" data-id="element-166">Venezuela es uno de esos países.</p>
          <p className="mb-4" data-id="element-167">
            Durante años te han dicho que "el problema" es político.
          </p>
          <p className="mb-4" data-id="element-168">
            Que todo se reduce a ideologías, líderes, errores internos, o una
            lista interminable de culpas.
          </p>
          <p className="mb-4" data-id="element-169">Eso es la superficie.</p>
          <p className="mb-4" data-id="element-170">Debajo hay otra historia.</p>
          <p className="mb-4" data-id="element-171">
            Una historia donde la energía no es un tema más.
          </p>
          <p className="text-xl font-bold text-[#1C1917] mb-4" data-id="element-172">
            Es el idioma del poder.
          </p>
          <p className="mb-4" data-id="element-173">
            Porque cuando la energía cambia, cambian las alianzas. Cambian las
            urgencias. Cambian los silencios. Cambian las reglas.
          </p>
          <p className="mb-4" data-id="element-174">
            Y en ese cambio, los minerales estratégicos no son un dato curioso.
          </p>
          <p className="mb-4" data-id="element-175">Son el centro de gravedad.</p>
          <p className="mb-4" data-id="element-176">
            La transición energética, la competencia tecnológica, las cadenas de
            suministro, la industria militar, los bloques económicos…
          </p>
          <p className="mb-4" data-id="element-177">Todo eso tiene una base material.</p>
          <p className="mb-4" data-id="element-178">
            Lo que está bajo tierra define lo que pasa sobre la mesa.
          </p>
          <p className="mb-4" data-id="element-179">
            Entonces, si alguien te explica Venezuela sin hablar de energía y
            minerales, te está contando una historia incompleta.
          </p>
          <p className="mb-4" data-id="element-180">No necesariamente por maldad.</p>
          <p className="mb-4" data-id="element-181">
            A veces por ignorancia. A veces por comodidad. A veces por agenda.
          </p>
          <p className="mb-4" data-id="element-182">Pero incompleta al fin.</p>
        </FadeIn>
      </section>

      {/* Origin Story */}
      <section className="space-y-4 bg-[#F0EBE0] -mx-6 px-6 py-12 md:-mx-12 md:px-12 rounded-sm border-y border-[#1C1917]/5" data-id="element-183">
        <FadeIn data-id="element-184">
          <h2 className="text-2xl font-sans font-bold text-[#1C1917] mb-6" data-id="element-185">
            "Minerales de Sangre" nace de una incomodidad
          </h2>
          <p className="mb-4" data-id="element-186">
            Este libro no nació de una "idea de contenido".
          </p>
          <p className="mb-4" data-id="element-187">Nació de una incomodidad real.</p>
          <p className="mb-4" data-id="element-188">
            De ver a Venezuela reducida a titulares. De escuchar debates que
            parecían intensos, pero no llegaban al fondo. De notar que se
            hablaba de política interna como si fuera el único motor, mientras
            el tablero global quedaba fuera del encuadre.
          </p>
          <p className="mb-4" data-id="element-189">
            Y llegó un momento en que esa incomodidad se volvió una pregunta
            simple:
          </p>
          <p className="font-bold italic text-[#7F1D1D] mb-4" data-id="element-190">
            ¿Por qué casi nadie está explicando a Venezuela desde la energía?
          </p>
          <p className="mb-4" data-id="element-191">Ahí apareció el "click".</p>
          <p className="mb-4" data-id="element-192">
            Sin energía, no hay siglo XXI. Sin minerales, no hay transición
            energética. Sin tablero, no hay comprensión.
          </p>
          <p className="mb-4" data-id="element-193">
            Y sin comprensión, lo que queda es lo que ya conoces: la pelea
            eterna de narrativas.
          </p>
          <p className="mb-4" data-id="element-194">Este libro es la salida de esa pelea.</p>
          <p className="mb-4" data-id="element-195">No para reemplazar una propaganda con otra.</p>
          <p className="mb-4" data-id="element-196">Sino para darte algo más útil:</p>
          <p className="text-xl font-bold uppercase tracking-widest text-center py-4" data-id="element-197">
            UN MARCO.
          </p>
        </FadeIn>
      </section>

      {/* Not Ideology */}
      <section className="space-y-4" data-id="element-198">
        <FadeIn data-id="element-199">
          <h2 className="text-3xl font-sans font-bold text-[#7F1D1D] mb-8" data-id="element-200">
            No es ideología. Es interés.
          </h2>
          <p className="mb-4" data-id="element-201">
            "Minerales de Sangre" no está escrito para convencerte.
          </p>
          <p className="mb-4" data-id="element-202">Está escrito para devolverte criterio.</p>
          <p className="mb-4" data-id="element-203">Eso exige una decisión: no caer en consignas.</p>

          <div className="grid md:grid-cols-2 gap-8 my-12" data-id="element-204">
            <div className="bg-white p-6 border border-[#1C1917]/10 shadow-sm" data-id="element-205">
              <h4 className="font-sans font-bold text-[#7F1D1D] mb-4 uppercase text-sm tracking-wider" data-id="element-206">
                Aquí no vas a encontrar:
              </h4>
              <ul className="space-y-3" data-id="element-207">
                <li className="flex items-start text-base" data-id="element-208">
                  <span className="mr-2 text-[#7F1D1D]" data-id="element-209">✕</span> gritos
                </li>
                <li className="flex items-start text-base" data-id="element-210">
                  <span className="mr-2 text-[#7F1D1D]" data-id="element-211">✕</span> "buenos vs.
                  malos" de caricatura
                </li>
                <li className="flex items-start text-base" data-id="element-212">
                  <span className="mr-2 text-[#7F1D1D]" data-id="element-213">✕</span> frases
                  diseñadas para viralizarse
                </li>
                <li className="flex items-start text-base" data-id="element-214">
                  <span className="mr-2 text-[#7F1D1D]" data-id="element-215">✕</span>{' '}
                  simplificaciones que te hacen sentir seguro pero te dejan
                  ciego
                </li>
              </ul>
            </div>

            <div className="bg-[#1C1917] p-6 text-[#F9F7F1]" data-id="element-216">
              <h4 className="font-sans font-bold text-white mb-4 uppercase text-sm tracking-wider" data-id="element-217">
                Aquí vas a encontrar:
              </h4>
              <p className="text-white mb-4 font-bold text-lg" data-id="element-218">Explicación.</p>
              <p className="text-gray-300 text-base" data-id="element-219">
                Una explicación basada en intereses, incentivos y estructuras.
              </p>
            </div>
          </div>

          <p className="mb-4" data-id="element-220">
            Porque el poder no se comporta como un sermón. Se comporta como un
            sistema.
          </p>
          <p className="mb-4" data-id="element-221">
            Y si entiendes el sistema, cambian tus preguntas.
          </p>
          <p className="mb-4" data-id="element-222">Cambian tus conclusiones.</p>
          <p className="mb-4" data-id="element-223">Cambia la manera en que lees el mundo.</p>
        </FadeIn>
      </section>

      {/* Unique Mechanism */}
      <section className="space-y-4" data-id="element-224">
        <FadeIn data-id="element-225">
          <h2 className="text-2xl font-sans font-bold text-[#1C1917] mb-6" data-id="element-226">
            El "Mecanismo Único" que te ha faltado
          </h2>
          <p className="mb-4" data-id="element-227">El problema no es que "nadie dijo nada".</p>
          <p className="mb-4" data-id="element-228">
            El problema es que casi nadie lo dijo con estructura narrativa
            geopolítica.
          </p>

          <p className="mb-4" data-id="element-229">Te dieron:</p>

          <ul className="list-disc pl-5 space-y-2 marker:text-[#7F1D1D] mb-4" data-id="element-230">
            <li data-id="element-231">datos sin marco</li>
            <li data-id="element-232">titulares sin contexto</li>
            <li data-id="element-233">indignación sin mapa</li>
            <li data-id="element-234">análisis sin conexión</li>
          </ul>

          <p className="mb-4" data-id="element-235">Lo que te faltó fue el orden.</p>

          <div className="bg-white p-6 border border-[#1C1917]/10 shadow-sm my-6" data-id="element-236">
            <p className="mb-4 font-semibold text-[#1C1917]" data-id="element-237">
              El libro te muestra:
            </p>

            <ul className="space-y-3" data-id="element-238">
              <li className="flex items-start" data-id="element-239">
                <Check className="w-5 h-5 text-[#7F1D1D] mr-3 mt-1 flex-shrink-0" data-id="element-240" />
                <span data-id="element-241">cómo los minerales definen poder</span>
              </li>
              <li className="flex items-start" data-id="element-242">
                <Check className="w-5 h-5 text-[#7F1D1D] mr-3 mt-1 flex-shrink-0" data-id="element-243" />
                <span data-id="element-244">cómo la energía dicta diplomacia</span>
              </li>
              <li className="flex items-start" data-id="element-245">
                <Check className="w-5 h-5 text-[#7F1D1D] mr-3 mt-1 flex-shrink-0" data-id="element-246" />
                <span data-id="element-247">
                  cómo encajan sanciones, contratos y dependencia en un mismo
                  tablero
                </span>
              </li>
              <li className="flex items-start" data-id="element-248">
                <Check className="w-5 h-5 text-[#7F1D1D] mr-3 mt-1 flex-shrink-0" data-id="element-249" />
                <span data-id="element-250">
                  por qué Venezuela es pieza estratégica, no caricatura
                </span>
              </li>
            </ul>
          </div>

          <p className="mb-4" data-id="element-251">
            Este marco tiene un nombre, porque te conviene recordarlo:
          </p>

          <p className="text-2xl font-bold text-center py-4 font-sans text-[#7F1D1D]" data-id="element-252">
            "El Mapa Bajo Tierra".
          </p>

          <p className="mb-4" data-id="element-253">Cada capítulo construye ese mapa.</p>
          <p className="mb-4" data-id="element-254">No para que memorices. Para que veas.</p>
          <p className="mb-4" data-id="element-255">
            Porque cuando ves el tablero, pasa algo particular:
          </p>
          <p className="font-bold text-center uppercase mb-4" data-id="element-256">
            no puedes dejar de verlo.
          </p>
        </FadeIn>
      </section>

      {/* Metaphors */}
      <section className="space-y-8" data-id="element-257">
        <FadeIn data-id="element-258">
          <h2 className="text-3xl font-sans font-bold text-[#7F1D1D] mb-8 border-b border-[#1C1917]/10 pb-4" data-id="element-259">
            Metáforas que te ponen la realidad enfrente
          </h2>
          <p data-id="element-260">Hay tres ideas que sostienen la lectura de este libro.</p>

          <div className="space-y-8 my-8" data-id="element-261">
            <div className="flex gap-4" data-id="element-262">
              <span className="text-4xl font-bold text-[#7F1D1D]/20 font-sans" data-id="element-263">
                1
              </span>
              <p data-id="element-264">
                <span className="font-bold text-[#1C1917]" data-id="element-265">
                  Venezuela no es el incendio. Es el pozo de gasolina debajo del
                  edificio.
                </span>{' '}
                Lo visible es caótico. Lo invisible es estratégico.
              </p>
            </div>
            <div className="flex gap-4" data-id="element-266">
              <span className="text-4xl font-bold text-[#7F1D1D]/20 font-sans" data-id="element-267">
                2
              </span>
              <p data-id="element-268">
                <span className="font-bold text-[#1C1917]" data-id="element-269">
                  La guerra energética no se pelea con tanques, sino con
                  contratos, sanciones y mapas.
                </span>{' '}
                El conflicto moderno no siempre hace ruido. Pero decide.
              </p>
            </div>
            <div className="flex gap-4" data-id="element-270">
              <span className="text-4xl font-bold text-[#7F1D1D]/20 font-sans" data-id="element-271">
                3
              </span>
              <p data-id="element-272">
                <span className="font-bold text-[#1C1917]" data-id="element-273">
                  Los minerales son la sangre del sistema moderno.
                </span>{' '}
                Donde fluyen, hay poder. Y donde el flujo se controla, el poder
                se concentra.
              </p>
            </div>
          </div>

          <p data-id="element-274">Estas metáforas no están para adornar.</p>
          <p data-id="element-275">
            Están para que puedas explicar lo complejo sin perder precisión.
          </p>
        </FadeIn>
      </section>

      {/* What you will understand */}
      <section className="space-y-6" data-id="element-276">
        <FadeIn data-id="element-277">
          <h2 className="text-2xl font-sans font-bold text-[#1C1917] mb-6" data-id="element-278">
            Qué vas a poder entender al terminar "Minerales de Sangre"
          </h2>
          <p data-id="element-279">
            Si hoy sientes que tienes piezas sueltas, este libro está diseñado
            para unirlas.
          </p>
          <p data-id="element-280">Vas a entender:</p>
          <ul className="space-y-4 my-6" data-id="element-281">
            <li className="pl-4 border-l-2 border-[#7F1D1D]" data-id="element-282">
              Por qué Venezuela sigue siendo central, incluso cuando el relato
              popular la describe como "periférica" o "aislada".
            </li>
            <li className="pl-4 border-l-2 border-[#7F1D1D]" data-id="element-283">
              Cómo funcionan las sanciones más allá del discurso moral, y por
              qué suelen operar como herramientas de orden global, no como
              "castigos" simples.
            </li>
            <li className="pl-4 border-l-2 border-[#7F1D1D]" data-id="element-284">
              Qué rol juegan actores globales (EE. UU., China, Europa y también
              actores regionales) cuando el tema de fondo es energía y acceso a
              recursos.
            </li>
            <li className="pl-4 border-l-2 border-[#7F1D1D]" data-id="element-285">
              Cómo la transición energética reconfigura el poder, y por qué eso
              vuelve más valiosos ciertos territorios, rutas y reservas.
            </li>
            <li className="pl-4 border-l-2 border-[#7F1D1D]" data-id="element-286">
              Por qué el concepto de soberanía cambia cuando el subsuelo vale
              más que la bandera, y cuando el valor de un país se mide en su
              capacidad de abastecer cadenas estratégicas.
            </li>
          </ul>
          <p data-id="element-287">No es un libro cómodo.</p>
          <p data-id="element-288">Pero sí es un libro claro.</p>
          <p data-id="element-289">Y claridad, hoy, vale más que mil opiniones.</p>
        </FadeIn>
      </section>

      {/* Who is this for */}
      <section className="space-y-6 bg-[#1C1917] text-[#F9F7F1] -mx-6 px-6 py-12 md:-mx-12 md:px-12 rounded-sm" data-id="element-290">
        <FadeIn data-id="element-291">
          <h2 className="text-2xl font-sans font-bold text-white mb-8" data-id="element-292">
            Para quién es este libro (y para quién no)
          </h2>

          <div className="grid md:grid-cols-2 gap-12" data-id="element-293">
            <div className="bg-white p-6 border border-[#1C1917]/10 shadow-sm" data-id="element-294">
              <h3 className="font-sans font-bold text-[#1C1917] mb-4 uppercase tracking-wider text-sm" data-id="element-295">
                Es para ti si:
              </h3>
              <ul className="space-y-3 text-[#1C1917]" data-id="element-296">
                <li className="flex items-start" data-id="element-297">
                  <Check className="w-4 h-4 mr-2 mt-1.5 text-[#7F1D1D]" data-id="element-298" />{' '}
                  quieres criterio propio
                </li>
                <li className="flex items-start" data-id="element-299">
                  <Check className="w-4 h-4 mr-2 mt-1.5 text-[#7F1D1D]" data-id="element-300" /> ya no
                  confías en explicaciones fáciles
                </li>
                <li className="flex items-start" data-id="element-301">
                  <Check className="w-4 h-4 mr-2 mt-1.5 text-[#7F1D1D]" data-id="element-302" />{' '}
                  sospechas que "algo grande" está pasando y quieres entenderlo
                  sin paranoia
                </li>
                <li className="flex items-start" data-id="element-303">
                  <Check className="w-4 h-4 mr-2 mt-1.5 text-[#7F1D1D]" data-id="element-304" /> te
                  interesa la geopolítica, la energía, la economía y el poder
                  global
                </li>
                <li className="flex items-start" data-id="element-305">
                  <Check className="w-4 h-4 mr-2 mt-1.5 text-[#7F1D1D]" data-id="element-306" />{' '}
                  necesitas un lenguaje adulto para hablar de Venezuela sin
                  propaganda
                </li>
              </ul>
            </div>

            <div className="bg-white p-6 border border-[#1C1917]/10 shadow-sm" data-id="element-307">
              <h3 className="font-sans font-bold text-[#1C1917] mb-4 uppercase tracking-wider text-sm" data-id="element-308">
                No es para ti si:
              </h3>
              <ul className="space-y-3 text-[#1C1917]" data-id="element-309">
                <li className="flex items-start" data-id="element-310">
                  <span className="mr-2 text-[#7F1D1D]" data-id="element-311">✕</span> buscas
                  propaganda lista para compartir
                </li>
                <li className="flex items-start" data-id="element-312">
                  <span className="mr-2 text-[#7F1D1D]" data-id="element-313">✕</span> quieres
                  respuestas simples para problemas complejos
                </li>
                <li className="flex items-start" data-id="element-314">
                  <span className="mr-2 text-[#7F1D1D]" data-id="element-315">✕</span> prefieres
                  consignas a estructura
                </li>
                <li className="flex items-start" data-id="element-316">
                  <span className="mr-2 text-[#7F1D1D]" data-id="element-317">✕</span> quieres que el
                  libro te diga "quién tiene razón", en lugar de enseñarte a
                  leer el tablero
                </li>
              </ul>
            </div>
          </div>
        </FadeIn>
      </section>

      {/* The Promise */}
      <section className="space-y-6" data-id="element-318">
        <FadeIn data-id="element-319">
          <h2 className="text-2xl font-sans font-bold text-[#1C1917] mb-6" data-id="element-320">
            La promesa, dicha sin adornos
          </h2>
          <p data-id="element-321">"Minerales de Sangre" te promete algo concreto:</p>
          <p className="font-bold" data-id="element-322">
            Entenderás el rol real de Venezuela en la guerra energética global y
            cómo su soberanía ha sido asediada, sin necesidad de propaganda ni
            consignas.
          </p>
          <p data-id="element-323">Eso no significa que saldrás "con una opinión".</p>
          <p className="mb-4" data-id="element-324">Significa que saldrás con algo mejor:</p>
          <p className="text-xl font-bold uppercase text-center py-4" data-id="element-325">
            UN MARCO.
          </p>
          <p data-id="element-326">Un marco que te permite leer noticias con otros ojos.</p>
          <p className="mb-4" data-id="element-327">Y cuando eso pasa, cambia todo:</p>
          <ul className="list-disc pl-5 space-y-2 marker:text-[#7F1D1D]" data-id="element-328">
            <li data-id="element-329">dejas de discutir por reflejo</li>
            <li data-id="element-330">
              dejas de depender de un influencer o un panelista para "pensar"
            </li>
            <li data-id="element-331">empiezas a hacer preguntas mejores</li>
            <li data-id="element-332">empiezas a ver patrones donde antes había caos</li>
          </ul>
        </FadeIn>
      </section>

      {/* Why others failed */}
      <section className="space-y-6" data-id="element-333">
        <FadeIn data-id="element-334">
          <h2 className="text-2xl font-sans font-bold text-[#1C1917] mb-6" data-id="element-335">
            Por qué lo demás no te dio esto
          </h2>
          <p data-id="element-336">
            Si has intentado entender por otras vías, tiene sentido que te
            sientas frustrado.
          </p>

          <div className="space-y-6 my-8" data-id="element-337">
            <div data-id="element-338">
              <h4 className="font-bold text-[#1C1917] font-sans" data-id="element-339">
                Noticias tradicionales
              </h4>
              <p className="text-base" data-id="element-340">
                Son fragmentadas. Son reactivas. Dependen de líneas editoriales.
                Te informan de "lo que pasó". No te explican "por qué se mueve
                el tablero".
              </p>
            </div>
            <div data-id="element-341">
              <h4 className="font-bold text-[#1C1917] font-sans" data-id="element-342">
                Opinión política
              </h4>
              <p className="text-base" data-id="element-343">
                Está diseñada para convencer. Funciona con emoción, identidad y
                pertenencia. Te da un bando, no un mapa.
              </p>
            </div>
            <div data-id="element-344">
              <h4 className="font-bold text-[#1C1917] font-sans" data-id="element-345">
                Documentales o papers académicos
              </h4>
              <p className="text-base" data-id="element-346">
                A veces son superficiales. A veces son técnicos hasta volverse
                intransitables. Y muchas veces no están escritos para conectarte
                con el lector real que quiere comprender sin especializarse.
              </p>
            </div>
          </div>

          <div className="bg-[#F0EBE0] p-6 border-l-4 border-[#7F1D1D]" data-id="element-347">
            <p className="font-bold mb-2" data-id="element-348">Resultado:</p>
            <p data-id="element-349">mucho contenido</p>
            <p data-id="element-350">poca comprensión</p>
          </div>

          <p data-id="element-351">Este libro existe para cerrar esa brecha.</p>
        </FadeIn>
      </section>

      {/* Bold Claim */}
      <section className="space-y-6" data-id="element-352">
        <FadeIn data-id="element-353">
          <h2 className="text-2xl font-sans font-bold text-[#1C1917] mb-6" data-id="element-354">
            El "bold claim" que sostiene todo
          </h2>
          <p data-id="element-355">Aquí está la frase que resume el enfoque:</p>
          <blockquote className="text-2xl font-serif italic text-[#7F1D1D] text-center my-8 leading-relaxed" data-id="element-356">
            "Si entiendes los minerales, entiendes la guerra. Si entiendes la
            energía, entiendes a Venezuela."
          </blockquote>
          <p data-id="element-357">No porque Venezuela sea "todo".</p>
          <p data-id="element-358">
            Sino porque el siglo XXI se organiza alrededor de recursos,
            dependencia y poder material.
          </p>
          <p data-id="element-359">
            Y Venezuela, por su historia y su subsuelo, está sentada en una
            silla estratégica.
          </p>
          <p data-id="element-360">Puedes ignorar esa silla.</p>
          <p className="font-bold" data-id="element-361">Pero el tablero no la ignora.</p>
        </FadeIn>
      </section>

      {/* What makes it different */}
      <section className="space-y-6" data-id="element-362">
        <FadeIn data-id="element-363">
          <h2 className="text-2xl font-sans font-bold text-[#1C1917] mb-6" data-id="element-364">
            Qué hace este libro diferente (sin prometer magia)
          </h2>
          <p data-id="element-365">
            "Minerales de Sangre" hace tres cosas que rara vez vienen juntas:
          </p>
          <ul className="space-y-4 my-6" data-id="element-366">
            <li className="flex items-start" data-id="element-367">
              <span className="font-bold text-[#7F1D1D] mr-3" data-id="element-368">1.</span>
              <span data-id="element-369">
                Conecta energía, geopolítica y poder en una sola narrativa
                coherente.
              </span>
            </li>
            <li className="flex items-start" data-id="element-370">
              <span className="font-bold text-[#7F1D1D] mr-3" data-id="element-371">2.</span>
              <span data-id="element-372">
                Traduce complejidad a mapas mentales claros, sin convertirlo en
                propaganda.
              </span>
            </li>
            <li className="flex items-start" data-id="element-373">
              <span className="font-bold text-[#7F1D1D] mr-3" data-id="element-374">3.</span>
              <span data-id="element-375">
                Te devuelve criterio: no te dice qué pensar, te muestra cómo se
                juega.
              </span>
            </li>
          </ul>
          <p data-id="element-376">No se lee como un "post".</p>
          <p data-id="element-377">Se lee como un mapa.</p>
          <p data-id="element-378">Y un mapa no te obliga a ir a un lugar.</p>
          <p data-id="element-379">Solo te impide perderte.</p>
        </FadeIn>
      </section>

      {/* The Invitation */}
      <section className="space-y-6" data-id="element-380">
        <FadeIn data-id="element-381">
          <h2 className="text-2xl font-sans font-bold text-[#1C1917] mb-6" data-id="element-382">
            La invitación
          </h2>
          <p data-id="element-383">Si alguna vez sentiste que:</p>
          <ul className="list-disc pl-5 space-y-2 marker:text-[#7F1D1D]" data-id="element-384">
            <li data-id="element-385">la historia de Venezuela estaba incompleta</li>
            <li data-id="element-386">el debate global omitía algo esencial</li>
            <li data-id="element-387">la energía era la clave que nadie explicaba</li>
            <li data-id="element-388">
              te cansaste de discutir narrativas y quieres entender intereses
            </li>
          </ul>
          <p className="mt-6" data-id="element-389">Entonces este libro es para ti.</p>
          <p data-id="element-390">No para convencerte.</p>
          <p data-id="element-391">Para darte las piezas.</p>
          <p data-id="element-392">
            Para que puedas leer el tablero sin que te lo traduzcan con agenda.
          </p>
          <p data-id="element-393">Para que vuelvas a confiar en tu criterio.</p>
        </FadeIn>
      </section>
    </article>;
}